import React, { useMemo, useState } from 'react';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { Box } from '../../../../../../CommonComponents/OPL/Box/Box';
import { Icon } from '../../../../../../CommonComponents/OPL/Icon/Icon';
import { TaskIcons } from '../../Tasks.constants';
import { Text } from '../../../../../../CommonComponents/OPL/Text/Text';
import { IconButton } from '../../../../../../CommonComponents/OPL/IconButton/IconButton';
import { Checkbox } from '../../../../../../CommonComponents/OPL/Checkbox/Checkbox';
import { PlanActionStepDTO } from '../../../../../types/planActionStepsDTO';
import { useFpId } from '../../../../../hooks/useFpId';
import { useRemoveTaskMutation } from '../../api/useRemoveTaskMutation';
import { useUpdateActionStepStateMutation } from '../../api/useUpdateActionStepStateMutation';
import { AddEditActionStep } from '../AddEditActionStep/AddEditActionStep';
import { useFinancialPlanQuery } from '../../../../../api/useFinancialPlanQuery';
import { TaskItemLoading } from './TaskItem.loading';
import { toast } from '../../../../../../CommonComponents/OPL/Toast/Toast';
import {
  isAdviserArea as selectIsAdviserArea,
  selectCurrentClient,
} from '../../../../../../CommonSelectors/CommonAreaSelector';
import { PulseCheckCategoryIcon } from '../PulseCheckCategoryIcon/PulseCheckCategoryIcon';
import { PulseCheckCategory } from '../../../../../types/goalsDTO';
import { useActionStepsQuery } from '../../../../../../ActionStepsReport/api/useActionStepsQuery';
import { PlanActionStepsAdvisersAssigneesDTO } from '../../../../../types/planActionStepsAdvisersAssigneesDTO';

interface TaskItemProps extends PlanActionStepDTO {
  isReport?: boolean;
  isPrint?: boolean;
  advisorAssignees?: PlanActionStepsAdvisersAssigneesDTO | null;
  pulseCheckCategory?: PulseCheckCategory;
  showAssetBucketing?: boolean;
  availableAssetBuckets?: { Title: string }[];
}

export const TaskItem: React.FC<TaskItemProps> = actionStep => {
  const {
    Icon: icon,
    Text: text,
    IsComplete: isComplete,
    GoalId: goalId,
    ActionStepKey: actionStepKey,
    IsTask: isTask,
    AssigneeId: assigneeId,
    AssigneeType: assignedType,
    DueDate: dueDate,
    pulseCheckCategory,
    isReport,
    isPrint,
    advisorAssignees,
  } = actionStep;

  const [isCurrentActionStepLoading, setIsCurrentActionStepLoading] = useState(false);

  const { isFetching } = isReport ? useActionStepsQuery() : useFinancialPlanQuery();

  const [showEditForm, setShowEditForm] = useState(false);

  const currentClient = useSelector(selectCurrentClient);

  const isAdvisor = useSelector(selectIsAdviserArea);

  const fpId = useFpId();

  const {
    mutateAsync: removeActionStep,
    isLoading: isLoadingRemoveActionStep,
  } = useRemoveTaskMutation();

  const {
    mutateAsync: updateTask,
    isLoading: isLoadingUpdateTask,
  } = useUpdateActionStepStateMutation();

  const assigneeName = useMemo(() => {
    switch (assignedType) {
      case 0:
        return `${currentClient.FirstName} ${currentClient.LastName}`;
      case 1:
        return `${currentClient.PartnerName}`;
      case 2: {
        const advisor = (advisorAssignees || []).find(({ AdviserId }) => AdviserId === assigneeId);

        return advisor ? `${advisor.FirstName} ${advisor.LastName}` : '';
      }
      default:
        return '';
    }
  }, [
    advisorAssignees,
    assignedType,
    assigneeId,
    currentClient.FirstName,
    currentClient.LastName,
    currentClient.PartnerName,
  ]);

  if (showEditForm) {
    return (
      <AddEditActionStep
        setFormVisibility={setShowEditForm}
        setIsCurrentActionStepLoading={setIsCurrentActionStepLoading}
        {...actionStep}
      />
    );
  }

  if (isFetching && isCurrentActionStepLoading) {
    return <TaskItemLoading icon={icon} isReport={isReport} />;
  }

  return (
    <Box display="flex" justifyContent="space-between" mb={isReport ? undefined : '24'}>
      <Box display="flex" alignItems="flex-start" mr="16" pt="2">
        {typeof pulseCheckCategory === 'number' ? (
          <PulseCheckCategoryIcon category={pulseCheckCategory} mr="8" mt={1} />
        ) : (
          <Icon mr="8" className={TaskIcons[icon || 0]} color="neutral.500" mt={1} />
        )}

        <Box display="flex" flexDirection="column">
          <Text style={{ textDecoration: isReport && isComplete ? 'line-through' : 'none' }}>
            {text}
          </Text>

          {assigneeName && !(isReport && isComplete) && (
            <Text>
              Assigned Person: <strong>{assigneeName}</strong>
            </Text>
          )}

          {dueDate && !(isReport && isComplete) && (
            <Text>
              Due Date: <strong>{moment(dueDate).format('MM/DD/YYYY')}</strong>
            </Text>
          )}
        </Box>
      </Box>

      <Box display="flex" alignItems="center" flexShrink={0}>
        {isTask && !isPrint && (
          <>
            <Checkbox
              disabled={isLoadingUpdateTask}
              checked={!!isComplete}
              onChange={async () => {
                try {
                  await updateTask({
                    GoalId: goalId,
                    ActionStepKey: actionStepKey,
                    clientId: currentClient.Id,
                    fpId,
                    Text: text,
                    Completion: !isComplete,
                    isReport,
                  });
                } catch (err) {
                  if (err instanceof Error) {
                    toast.error(err.message);
                  }
                }
              }}
              aria-label={isComplete ? 'Mark action step as todo' : 'Mark action step as done'}
              ml="4"
            />

            {isAdvisor && (
              <IconButton
                icon="fas fa-pencil-alt"
                size="small"
                variant="subtleInactive"
                onClick={() => {
                  setShowEditForm(true);
                }}
                aria-label="Edit action step"
                ml="4"
              />
            )}
          </>
        )}

        {isAdvisor && (
          <IconButton
            icon="far fa-trash-alt"
            size="small"
            variant="subtleInactive"
            disabled={isLoadingRemoveActionStep}
            onClick={async () => {
              try {
                await removeActionStep({
                  GoalId: goalId,
                  ActionStepKey: actionStepKey,
                  clientId: currentClient.Id,
                  fpId,
                  isReport,
                });
              } catch (err) {
                if (err instanceof Error) {
                  toast.error(err.message);
                }
              }
            }}
            aria-label="Delete action step"
            ml="4"
          />
        )}
      </Box>
    </Box>
  );
};
